import type { LicenseState } from '@lastrouter/contracts';
import { Card, CardContent, CardHeader, CardTitle } from '@lastrouter/ui';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../api/hooks.js';
import { linkClass, PageHeader, QueryState, StatTile, StatusBadge } from '../components/common.js';
import { formatCompact, formatDateTime } from '../lib/format.js';

export function licenseTone(status: LicenseState['status']) {
  return status === 'valida' ? 'success' : status === 'tolerancia' ? 'warning' : status === 'ausente' ? 'neutral' : 'danger';
}

export function OverviewPage() {
  const { t } = useTranslation();
  const q = useApiQuery('overview');
  return (
    <>
      <PageHeader title={t('overview.title')} subtitle={t('overview.period')} />
      <QueryState query={q} rows={6}>
        {(o) => {
          const d = o.last_7_days;
          return (
            <div className="flex flex-col gap-6">
              <section aria-label={t('overview.period')} className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <StatTile label={t('overview.calls')} value={formatCompact(d.calls)} />
                <StatTile label={t('overview.users')} value={formatCompact(d.users)} />
                <StatTile label={t('overview.answered')} value={formatCompact(d.answered)} />
                <StatTile label={t('overview.clarifications')} value={formatCompact(d.clarifications)} />
                <StatTile label={t('overview.errors')} value={formatCompact(d.errors)} />
                <StatTile label={t('overview.negative')} value={formatCompact(d.negative_feedback)} />
              </section>

              <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('overview.publication')}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-2">
                    {o.publication ? (
                      <>
                        <p className="font-mono text-[length:var(--lr-text-sm)]">{o.publication.id}</p>
                        <p className="text-fg-secondary">{t('overview.publishedBy', { by: o.publication.created_by, at: formatDateTime(o.publication.created_at) })}</p>
                        <p className="text-fg-secondary">{o.publication.commit ? t('overview.commit', { commit: o.publication.commit.slice(0, 10) }) : t('overview.noCommit')}</p>
                        {o.publication.reason ? <p>{o.publication.reason}</p> : null}
                      </>
                    ) : (
                      <p className="text-fg-secondary">{t('overview.noPublication')}</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('overview.license')}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-2">
                    <div>
                      <StatusBadge tone={licenseTone(o.license.status)}>{t(`license.${o.license.status}`)}</StatusBadge>
                    </div>
                    {o.license.cliente ? <p>{o.license.cliente}</p> : null}
                    <p className="text-fg-secondary">{o.license.detail}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('overview.connections')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="flex flex-col gap-2">
                      {o.connections.map((c) => (
                        <li key={c.id} className="flex flex-wrap items-center justify-between gap-2">
                          <span>
                            <span className="font-medium">{c.id}</span> <span className="text-fg-muted">{c.type}</span>
                          </span>
                          <StatusBadge tone={c.ok ? 'success' : 'danger'}>{c.ok ? t('connections.ok') : t('connections.failed')}</StatusBadge>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('overview.pending')}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-2">
                    {o.open_drafts === 0 && o.open_feedback === 0 ? <p className="text-fg-secondary">{t('overview.nothingPending')}</p> : null}
                    {o.open_drafts > 0 ? (
                      <Link to="/context" search={{ tab: 'drafts' }} className={linkClass}>
                        {t('overview.openDrafts', { count: o.open_drafts })}
                      </Link>
                    ) : null}
                    {o.open_feedback > 0 ? (
                      <Link to="/quality" search={{ tab: 'feedback' }} className={linkClass}>
                        {t('overview.openFeedback', { count: o.open_feedback })}
                      </Link>
                    ) : null}
                  </CardContent>
                </Card>
              </div>
            </div>
          );
        }}
      </QueryState>
    </>
  );
}
